import { GenericVerificationFlow } from "./GenericVerificationFlow"
import { useSecuritySectionStore } from "../stores/securitySectionStore"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { showErrorNotification } from "@components/ui/Notification/Notification"
import { useOTPMutations } from "@hooks/useOTPMutations"
import { getAuthToken } from "@services/authService"
import { useSecurityMutations } from "../hooks/useSecurityMutations"
import { MFA_METHODS } from "@utils/constants.util"

export const MFASMSSetup = () => {
	const { updateUser, goToIdle, setToken, token, user } =
		useSecuritySectionStore()
	const { sendPhoneVerificationSecured, validateOTP, isLoading } =
		useOTPMutations()
	const { updateSecurity } = useSecurityMutations()
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)

	const validatePhone = (value: string) => {
		if (!value) return dictionary?.mobilePhoneRequiredMessage || ""
		if (!/^\+?[1-9]\d{1,14}$/.test(value))
			return dictionary?.invalidPhoneFormatMessage || ""
		return null
	}

	const handleSend = async (phone: string) => {
		const sessionToken = getAuthToken()
		if (!sessionToken) {
			showErrorNotification(
				dictionary?.sessionExpiredMessage ?? "Please sign in again."
			)
			return false
		}
		try {
			const otp = await sendPhoneVerificationSecured.mutateAsync(phone)
			setToken(otp?.data?.token ?? "")
			return otp?.data?.success ?? false
		} catch {
			return false
		}
	}

	const handleVerify = async (otp: string, input: string) => {
		try {
			const otpValidation = await validateOTP.mutateAsync({
				token: token ?? "",
				otp
			})
			if (otpValidation?.data?.success) {
				await handleSuccess(input, otpValidation?.data?.token ?? "")
				return true
			}
		} catch {}
		return false
	}

	const handleSuccess = async (phone: string, token: string) => {
		const primaryMFA = MFA_METHODS.SMS
		try {
			await updateSecurity.mutateAsync({
				token: token ?? "",
				changedPhone: phone,
				setPhoneValidated: true,
				...(!user?.emailVerified ? { primaryMFA } : {})
			})
			updateUser({
				phone,
				phoneVerified: true,
				...(!user?.emailVerified ? { primaryMFA } : {})
			})
			goToIdle()
		} catch {
			showErrorNotification("Failed to update security settings")
		}
	}

	const handleResend = async (phone: string) => {
		const sessionToken = getAuthToken()
		if (!sessionToken) return
		await sendPhoneVerificationSecured.mutateAsync(phone)
	}

	return (
		<GenericVerificationFlow
			title={dictionary?.addMFAViaSMSText || ""}
			description={dictionary?.addMultiFactorViaSmsDescription || ""}
			inputLabel={dictionary?.mobilePhoneNumberLabel || ""}
			inputPlaceholder={dictionary?.enterYourNewEmailAddressLabel || ""}
			inputType="phone"
			validateInput={validatePhone}
			onSend={handleSend}
			onVerify={handleVerify}
			resendFunction={handleResend}
			isLoading={isLoading}
			goBack={goToIdle}
		/>
	)
}
